
import {
  computed,
  defineComponent,
  onMounted,
  reactive,
  ref,
  watch,
} from "vue";
import { GetRole, GetRoleType, GetUser } from "@/services";
import { useStore } from "@/store";
import { IState, IStateRoleGroup } from "./";
import UserRoleBehaviourList from "./RoleBehaviourList.vue";
import UserRoleCreateOrUpdate from "@/views/users/view/roles/CreateOrUpdate.vue";
import _ from "lodash";

export default defineComponent({
  name: "UserRoles",
  components: { UserRoleCreateOrUpdate, UserRoleBehaviourList },
  props: {
    detail: {
      type: Object as () => GetUser,
      required: true,
    },
    userId: {
      type: Number,
      default: () => 0,
    },
    definedRoles: {
      type: Array as () => GetRole[],
      default: () => [],
    },
  },
  emits: ["update:defined-components"],
  setup(props) {
    const refCreateOrUpdate = ref();

    const store = useStore();
    const allRoleTypes = computed<GetRoleType[]>(
      () => store.state.user.roleTypes
    );
    const state = reactive<IState>({
      roleGroups: [
        {
          title: "Committee Roles",
          type: "committee",
          types: [],
        },
        {
          title: "Centralized Roles",
          type: "centralized",
          types: [],
        },
        {
          title: "Project Roles",
          type: "project",
          types: [],
        },
        {
          title: "Staff Roles",
          type: "staff",
          types: [],
        },
      ],
      selectedRoleGroup: "committee",
    });

    const visibleRoleGroup = computed<IStateRoleGroup>(() => {
      const index = _.findIndex(state.roleGroups, {
        type: state.selectedRoleGroup,
      });
      const group = _.cloneDeep(
        _.get(state.roleGroups, index, {})
      ) as IStateRoleGroup;
      group.types = _.sortBy(group.types, (o: GetRoleType) => {
        return _.findIndex(props.definedRoles, { behaviour: o.behaviour }) > -1
          ? 0
          : 1;
      });
      return group;
    });

    const userDefinedRoles = computed<Record<string, any>[]>(() => []);

    const _init = () => {
      state.roleGroups.map((roleGroup) => {
        const types: GetRoleType[] = [];
        allRoleTypes.value.map((roleType) => {
          if (roleType.type === roleGroup.type) types.push(roleType);
        });
        roleGroup.types = types;
      });
    };

    onMounted(() => {
      if (allRoleTypes.value.length > 0) {
        _init();
      }
    });

    const unwatch = watch(
      allRoleTypes,
      () => {
        _init();
        unwatch();
      },
      {
        deep: true,
        flush: "post",
      }
    );

    const canEdit = computed<boolean>(() => {
      return _.get(props.detail, "abilities.can_update", false);
    });

    const onSelectRoleGroup = (roleGroup: IStateRoleGroup) => {
      state.selectedRoleGroup = roleGroup.type;
    };

    const onAddRole = (roleType: GetRoleType) => {
      if (refCreateOrUpdate.value) {
        refCreateOrUpdate.value.onOpen(roleType);
      }
    };

    const onEditRole = (roleType: GetRoleType, role: GetRole) => {
      if (refCreateOrUpdate.value) {
        refCreateOrUpdate.value.onOpen(roleType, role);
      }
    };

    const countScope = (type: string): number => {
      let count = 0;
      props.definedRoles.map((role) => {
        if (role.meta.type === type) count++;
      });
      return count;
    };

    return {
      refCreateOrUpdate,
      state,
      visibleRoleGroup,
      userDefinedRoles,
      canEdit,
      countScope,
      onSelectRoleGroup,
      onAddRole,
      onEditRole,
    };
  },
});
